/**
 * @file The SPA's auth provider: Better Auth, over the same-origin `/api/auth/*` endpoints.
 *
 * This is the only file in apps/web that names the auth library; everything else talks to the
 * `WebAuthProvider` interface (./types.ts). It runs the same locally and in production, because
 * the API behind it does: there is no fabricated development session.
 *
 * The browser holds no credential this code can read. Better Auth's session is an HttpOnly,
 * first-party cookie that the browser attaches to same-origin requests — which is why the SPA calls
 * the API under `/api` on its OWN origin instead of cross-origin (the Vite proxy locally, a Worker
 * route on the SPA's hostname in staging; see docs/AUTH-MIGRATION.md, Finding A). Nothing here
 * touches localStorage or sessionStorage.
 */
import { createAuthClient } from "better-auth/react";
import { useQueryClient } from "@tanstack/react-query";
import { AuthForm } from "../components/AuthScreen";
import { SecondaryButton } from "../components/ui";
import { useAuthValue } from "./context";
import type {
  WebAuthActions,
  WebAuthProvider,
  WebProfileActions,
  WebSecurityActions,
  WebSession,
} from "./types";

/**
 * The client. No `baseURL`: it defaults to the page's own origin, which is exactly the
 * same-origin shape the API is served in. The path matches `AUTH_BASE_PATH` in the API.
 */
export const authClient = createAuthClient({ basePath: "/api/auth" });

/** What to say when the library gives us nothing readable. Never a raw body. */
const GENERIC_FAILURE = "Something went wrong. Please try again.";

/**
 * A message safe to show a person, from the library's `{ error }` result.
 *
 * Wrong password and unknown email are the SAME answer on the server (it does not say which),
 * and this passes that through untouched rather than inventing a distinction.
 */
export function messageFromAuthError(
  error: { readonly message?: string | undefined; readonly status?: number | undefined } | null,
): string | null {
  if (error === null) return null;
  const message = (error.message ?? "").trim();
  if (message.length > 0) return message;
  if (error.status === 401) return "Wrong email or password.";
  return GENERIC_FAILURE;
}

/** Shown when a reset token is rejected: it was used, expired or never valid. */
export const INVALID_RESET_LINK = "This reset link is invalid or has expired.";
const RATE_LIMITED = "Too many attempts. Please wait a minute and try again.";

/** Where the emailed reset link lands: this SPA, on its OWN origin (the API checks it is trusted). */
function resetPageUrl(): string {
  return `${window.location.origin}/reset-password`;
}

/**
 * Account recovery over the library's client methods (`requestPasswordReset`, `resetPassword`,
 * `sendVerificationEmail`, which map to POST /request-password-reset, /reset-password and
 * /send-verification-email).
 */
const authActions: WebAuthActions = {
  async requestPasswordReset(email) {
    const result = await authClient.requestPasswordReset({ email, redirectTo: resetPageUrl() });
    if (result.error === null) return null;
    if (result.error.status === 429) return RATE_LIMITED;
    // Any other refusal says nothing about whether the account exists (the server answers a known
    // and an unknown address identically), so a generic message is safe and honest.
    return GENERIC_FAILURE;
  },

  async resetPassword({ token, newPassword }) {
    const result = await authClient.resetPassword({ newPassword, token });
    if (result.error === null) return null;
    if (result.error.status === 429) return RATE_LIMITED;
    if (result.error.status === 400 || result.error.status === 401 || result.error.status === 403) {
      // Invalid or expired token. A too-short password is caught before the call.
      return INVALID_RESET_LINK;
    }
    return GENERIC_FAILURE;
  },

  async sendVerificationEmail(email) {
    const result = await authClient.sendVerificationEmail({
      email,
      callbackURL: `${window.location.origin}/`,
    });
    if (result.error === null) return null;
    if (result.error.status === 429) return RATE_LIMITED;
    return GENERIC_FAILURE;
  },

  async signOut() {
    await authClient.signOut();
  },
};

function BetterAuthScreen() {
  return (
    <AuthForm
      onRequestPasswordReset={authActions.requestPasswordReset}
      onSignIn={async ({ email, password }) => {
        const result = await authClient.signIn.email({ email, password });
        return messageFromAuthError(result.error);
      }}
      onSignUp={async ({ name, email, password }) => {
        const result = await authClient.signUp.email({ name, email, password });
        return messageFromAuthError(result.error);
      }}
    />
  );
}

function BetterUserMenu() {
  const { session } = useAuthValue();
  const queryClient = useQueryClient();
  const who = session.user?.name.trim() || session.user?.email || "Signed in";

  return (
    <div className="flex items-center gap-3">
      <span className="max-w-[16rem] truncate text-sm text-muted-foreground" title={session.user?.email}>
        {who}
      </span>
      <SecondaryButton
        onClick={() => {
          void authActions.signOut().then(() => {
            // The next person to sign in on this tab must not see the last one's cached data.
            queryClient.clear();
          });
        }}
      >
        Sign out
      </SecondaryButton>
    </div>
  );
}

/** A Date (or ISO string, after a JSON round trip) as an ISO instant. */
function iso(value: Date | string): string {
  return new Date(value).toISOString();
}

/**
 * Password and session management over Better Auth's `/change-password`, `/list-sessions`,
 * `/revoke-session` and `/revoke-other-sessions`. Each mutation returns the library's own
 * message (through `messageFromAuthError`) or null, exactly like sign-in.
 */
const betterSecurity: WebSecurityActions = {
  async changePassword({ currentPassword, newPassword, revokeOtherSessions }) {
    const result = await authClient.changePassword({
      currentPassword,
      newPassword,
      revokeOtherSessions,
    });
    return messageFromAuthError(result.error);
  },

  async listSessions() {
    const [list, current] = await Promise.all([
      authClient.listSessions(),
      authClient.getSession(),
    ]);
    if (list.error !== null) throw new Error(messageFromAuthError(list.error) ?? GENERIC_FAILURE);
    const currentId = current.data?.session.id ?? null;
    return list.data.map((row) => ({
      id: row.id,
      token: row.token,
      userAgent: row.userAgent ?? null,
      createdAt: iso(row.createdAt),
      updatedAt: iso(row.updatedAt),
      current: row.id === currentId,
    }));
  },

  async revokeSession(token) {
    const result = await authClient.revokeSession({ token });
    return messageFromAuthError(result.error);
  },

  async revokeOtherSessions() {
    const result = await authClient.revokeOtherSessions();
    return messageFromAuthError(result.error);
  },
};

/**
 * Name and email edits over `authClient.updateUser` (POST /update-user; the server accepts only a
 * trimmed `name`) and `authClient.changeEmail` (POST /change-email, which mails a link to the NEW
 * address and changes nothing until it is opened). Both are the library's path-derived client method
 * names, confirmed against better-auth 1.7.7.
 */
const betterProfile: WebProfileActions = {
  async updateName(name) {
    const result = await authClient.updateUser({ name });
    if (result.error?.status === 429) return RATE_LIMITED;
    return messageFromAuthError(result.error);
  },

  async requestEmailChange({ newEmail, currentPassword }) {
    // `authClient.changeEmail`'s typed body has no `currentPassword` (the library does not know
    // about it; our server hook requires it), so the same endpoint is called through the client's
    // `$fetch`. The session cookie and origin handling are the same as every other client call.
    const result = await authClient.$fetch("/change-email", {
      method: "POST",
      body: { newEmail, currentPassword, callbackURL: `${window.location.origin}/` },
    });
    if (result.error?.status === 429) {
      return "You have asked for too many confirmation links. Please try again in an hour.";
    }
    return messageFromAuthError(result.error);
  },
};

export const betterWebAuth: WebAuthProvider = {
  useSession(): WebSession {
    const { data, isPending } = authClient.useSession();
    return {
      isLoaded: !isPending,
      isSignedIn: data !== null && data !== undefined,
      user:
        data === null || data === undefined
          ? null
          : {
              id: data.user.id,
              email: data.user.email,
              name: data.user.name,
              emailVerified: data.user.emailVerified === true,
            },
    };
  },

  ...authActions,

  AuthScreen: BetterAuthScreen,
  UserMenu: BetterUserMenu,
  security: betterSecurity,
  profile: betterProfile,
};
